import {Draggable, gsap} from "gsap/all";
import $ from "jquery";
import mazeData from "/data/cerveau-maze.json"
import {BaseGameMNS2} from "../base/BaseGameMNS2";

const SEPERATOR = "-"

const alpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const slotSize = 70;
const slotsStartAt = {x: 70, y: 245};
const slotsColumnCount = 12
const slotsRowCount = 9
const slotsCount = slotsColumnCount * slotsRowCount;
const defaultBounds = {top: slotsStartAt.y, left: slotsStartAt.x, width: (slotsStartAt.x + slotSize * slotsColumnCount), height: (slotsStartAt.y + slotSize * slotsRowCount)};

export class BrainMaze {

  constructor(el, reachedTargetCallback) {
    this.$el = $(el);
    this.reachedTargetCallback = reachedTargetCallback;
    this.$pion = this.$el.find("#brainmaze-pion");
    this.isShown = false;
    this.draggable = null;

    this.currentTimeline = null;
    this.locked = false;

    this.currentPath = null;
    this.currentTargetIndex = null;
    this.currentTargetCode = null;

    this.currentSlot = null;
    this.$currentSlot = null;
    this.currentSlotConstraints = null;

    this.diceSequence = [];

    this.init();
  }

  init() {

    Draggable.zIndex = 100;

    // Hide by default
    gsap.set(this.$el, {autoAlpha: 0});

    // Build slots
    for (let i = 0; i < slotsCount; i++) {
      const x = (i % slotsColumnCount) * slotSize + slotsStartAt.x
      const y = Math.floor(i / slotsColumnCount) * slotSize + slotsStartAt.y
      const code = alpha[(i % slotsColumnCount)] + (Math.floor(i / slotsColumnCount) + 1);
      const slotEl = $(`<div class='brainmaze-slot' data-slot-index="${i}" data-slot-code="${code}" data-x="${x}" data-y="${y}"></div>`);
      gsap.set(slotEl, {x: x, y: y});
      this.$el.find("#brainmaze-slots").append(slotEl);
    }

    // This is old school `this` scope handling for callback functions
    const _this = this;

    this.draggable = Draggable.create(this.$pion, {
      inertia: true,
      allowEventDefault: true,
      throwResistance: 5000,
      bounds: defaultBounds,
      onDrag: function (pointerEvent) {

        // Make sure that drag resets Still There timer
        app.resetTimer()

        // Free drag and there are no current slot, otherwise constrain
        let finalX = this.x;
        let finalY = this.y;
        if (_this.currentSlot) {

          // Define frame values
          const direction = _this.getDirectionForPoint(this.x, this.y)
          const {slotX, slotY, constrainLeft, constrainRight, constrainUp, constrainDown} = _this.currentSlotConstraints;

          if (_this.locked) {
            // Prevent drag on lock
            finalX = slotX;
            finalY = slotY;
          } else if (direction === "u" || direction === "d") {
            // Vertical, keep lock on x-axis
            finalX = slotX;
            finalY = constrain(this.y, constrainUp, constrainDown);
          } else if (direction === "l" || direction === "r") {
            // Horizontal, keep lock on y-axis
            finalX = constrain(this.x, constrainLeft, constrainRight);
            finalY = slotY;
          } else {
            // Full lock
            finalX = slotX;
            finalY = slotY;
          }
        }

        // Apply position values to pion
        gsap.set(this.target, {x: finalX, y: finalY});

        // Prevent drag on lock
        if (_this.locked) return;

        // Store real currentTargetIndex in local variable for easy access below
        const targetIndex = _this.currentTargetIndex

        // Find nearest slot
        const lookupConstrainLeft = (_this.currentSlotConstraints?.constrainLeft ?? Number.NEGATIVE_INFINITY)
        const lookupConstrainRight = (_this.currentSlotConstraints?.constrainRight ?? Number.POSITIVE_INFINITY)
        const lookupConstrainUp = (_this.currentSlotConstraints?.constrainUp ?? Number.NEGATIVE_INFINITY)
        const lookupConstrainDown = (_this.currentSlotConstraints?.constrainDown ?? Number.POSITIVE_INFINITY)
        let nearestSlot = _this.getSlotAtPoint(
          constrain(finalX, lookupConstrainLeft, lookupConstrainRight),
          constrain(finalY, lookupConstrainUp, lookupConstrainDown),
        )

        // Prevent reverse
        const currentPathIndex = _this.currentPath.findIndex((element, index) => (element.split(SEPERATOR).includes(_this.$currentSlot.data("slot-code"))));
        const nearestPathIndex = _this.currentPath.findIndex((element, index) => (element.split(SEPERATOR).includes($(nearestSlot).data("slot-code"))));
        if (nearestPathIndex !== undefined && nearestPathIndex < currentPathIndex) {
          // console.log("Prevent reverse");
          nearestSlot = null;
        }

        // Update current slot values
        if (nearestSlot && _this.currentSlot !== nearestSlot) {
          _this.selectSlot(nearestSlot);

          // Handles when the target is actually reached
          if (_this.$currentSlot.data("slot-index") === targetIndex) {
            app.audio.play("audio/MNS2_Int3.2 Cerveau a l_ecoute_SFX/MNS2_ME_INT3.2_SFXpion.mp3", 1, true)
            this.disable()
            _this.hideTarget(_this.reachedTargetCallback);
          }
        }

      },
      onDragEnd: function (pointerEvent) {
        if (!_this.currentSlot || _this.locked) return;

        // Snap to nearest zone
        const slotX = _this.$currentSlot.data("x");
        const slotY = _this.$currentSlot.data("y");
        gsap.to(this.target, {x: slotX, y: slotY, ease: "sine.out"});
      },
    })[0];

    // Default pion position
    this.reset();
  }

  selectSlot(slot) {
    this.previousSlot = $(this.currentSlot);
    this.currentSlot = slot;
    this.$currentSlot = $(slot);
    this.currentSlotConstraints = this.getCurrentSlotConstraints();
    $(".brainmaze-slot.active").removeClass("active");
    this.$currentSlot.addClass("active");
  }

  hideTarget(callback) {
    $(".brainmaze-slot.target").removeClass("target");
    gsap.to("#brainmaze-target", {
      scale: 0,
      ease: "sine.in",
      duration: 0.75,
      onComplete: () => {
        if (callback) callback()
      },
    })
  }

  getCurrentSlotConstraints() {
    const allowedDirections = {
      left: false,
      right: false,
      up: false,
      down: false
    };

    // Define which directions to allow in order to get to destination
    if (this.currentPath) {
      const slotIndexOnPath = this.currentPath.findIndex((item) => item.split(SEPERATOR).includes(this.$currentSlot.data("slot-code")))
      const nextSlotIndexOnPath = (slotIndexOnPath + 1 < this.currentPath.length) ? slotIndexOnPath + 1 : null;
      if (nextSlotIndexOnPath !== null) {
        const prevSlotDirection = this.previousSlot?.length > 0 ? this.getDirectionForPoint(this.previousSlot.data("x"), this.previousSlot.data("y")) : null;
        const nextSlotOptions = this.currentPath[nextSlotIndexOnPath].split(SEPERATOR)
        for (const nextSlotOption of nextSlotOptions) {
          const nextSlot = this.getSlotElForCode(nextSlotOption);
          const nextSlotDirection = nextSlot.length > 0 ? this.getDirectionForPoint(nextSlot.data("x"), nextSlot.data("y")) : null;
          allowedDirections.up = allowedDirections.up || ([nextSlotDirection, prevSlotDirection].includes("u"));
          allowedDirections.down = allowedDirections.down || ([nextSlotDirection, prevSlotDirection].includes("d"));
          allowedDirections.left = allowedDirections.left || ([nextSlotDirection, prevSlotDirection].includes("l"));
          allowedDirections.right = allowedDirections.right || ([nextSlotDirection, prevSlotDirection].includes("r"));
        }
      }
    }

    const slotX = this.$currentSlot.data("x");
    const slotY = this.$currentSlot.data("y");
    return {
      slotX: slotX,
      slotY: slotY,
      constrainLeft: (!allowedDirections.left ? slotX : slotX - slotSize),
      constrainRight: (!allowedDirections.right ? slotX : slotX + slotSize),
      constrainUp: (!allowedDirections.up ? slotY : slotY - slotSize),
      constrainDown: (!allowedDirections.down ? slotY : slotY + slotSize),
    }
  }

  getSlotAtPoint(x, y) {
    const elementsAtPoint = document.elementsFromPoint(x, y);
    for (const elementAtPoint of elementsAtPoint) {
      if (elementAtPoint && elementAtPoint.classList.contains("brainmaze-slot")) return elementAtPoint
    }
    return false;
  }

  getDirectionForPoint(x, y) {
    if (this.currentSlot) {
      const slotPosition = this.currentSlot.getBoundingClientRect();
      const point = {
        x: x - (slotPosition.x + slotPosition.width / 2),
        y: y - (slotPosition.y + slotPosition.height / 2)
      };

      const distance = Math.hypot(point.x, point.y);
      if (distance < 5) return null;

      const deg = (Math.atan2(point.y, point.x) * 180) / Math.PI;
      if (deg > -45 && deg < 45) return "r"
      else if (deg > -135 && deg < -45) return "u"
      else if (deg < -135 || deg > 135) return "l"
      else return "d"
    }
    return null;
  }

  enable() {
    this.draggable.enable();
  }

  setTarget(selectedResult) {
    console.log(selectedResult)
    console.log("setTarget() to", selectedResult.path[selectedResult.path.length - 1]);

    // Update internal variables
    this.currentPath = selectedResult.path;
    this.currentTargetCode = this.currentPath[this.currentPath.length - 1]
    this.currentTargetIndex = this.getSlotIndexForCode(this.currentTargetCode);
    this.previousSlot = null;
    this.currentSlotConstraints = this.getCurrentSlotConstraints();

    // For debug purpose only
    this.highlightPath();

    // Set current board
    const selectedType = (selectedResult.type.toUpperCase() === "S") ? "S" : "C";
    this.selectBoard(selectedType);

    $(".brainmaze-slot.target").removeClass("target");
    const $targetSlot = this.getSlotElForCode(this.currentTargetCode)
    $targetSlot.addClass("target");
    const targetPosition = {x: gsap.getProperty($targetSlot.get(0), "x"), y: gsap.getProperty($targetSlot.get(0), "y")}

    gsap.killTweensOf("#brainmaze-target");
    gsap.set("#brainmaze-target", {...targetPosition});
    gsap.fromTo("#brainmaze-target", {
      scale: 0,
    }, {
      scale: 1,
      ease: "bounce.out",
      duration: 0.5
    })
  }

  // Only use for debug purpose
  setDebugPosition(code = 'F6') {
    const initialSlot = $(`[data-slot-code='${code}']`).get(0)
    this.selectSlot(initialSlot);
    this.currentTargetCode = code;
    gsap.set(this.$pion, {x: gsap.getProperty(initialSlot, 'x'), y: gsap.getProperty(initialSlot, 'y')});
  }

  getSlotIndexForCode(slotCode) {
    const columnIndex = alpha.indexOf(slotCode[0].toUpperCase());
    const rowIndex = parseInt(slotCode[1]) - 1;
    return (rowIndex * slotsColumnCount) + columnIndex
  }

  getSlotElForCode(slotCode) {
    return $(`.brainmaze-slot[data-slot-index=${this.getSlotIndexForCode(slotCode)}]`);
  }

  selectBoard(type = "initial") {
    console.log("selectBoard type", type);
    gsap.set($(".boards #board-initial"), {autoAlpha: (type === "initial") ? 1 : 0});
    gsap.set($(".boards #board-stratium"), {autoAlpha: (type === "S") ? 1 : 0});
    gsap.set($(".boards #board-cortex"), {autoAlpha: (type === "C") ? 1 : 0});
  }

  bringPionToZone(zone) {
    const dest = (zone === "C") ? {x: 770, y: 525} : {x: 490, y: 525};
    gsap.to(this.$pion, {...dest, duration: 1});
  }

  togglePionVisibility(visibility) {
    gsap.set(this.$pion, {autoAlpha: visibility ? 1 : 0});
  }

  playEndAnimation(result, finalScoreC, finalScoreS) {
    this.locked = true;
    this.currentTimeline = gsap.timeline();

    console.log(`playEndAnimation: #big-end-animation-${result}-${app.i18n.lang}`, finalScoreC, finalScoreS)

    // Set visible
    this.currentTimeline.set(this.$el.find("#brainmaze-end-animation"), {autoAlpha: 1});
    this.currentTimeline.set(this.$el.find("#brainmaze-end-animation canvas"), {autoAlpha: 0});

    // Hide pink overlay added at last question feedback step
    // this.currentTimeline.to("#brainmaze-pink-overlay", {
    //   autoAlpha: 0
    // });

    // Big animation play
    this.currentTimeline.add(() => {
      gsap.set(this.$el.find(`#big-end-animation-${result}-${app.i18n.lang}`), {autoAlpha: 1});
      const endAnimLottie = this.$el.find(`#big-end-animation-${result}-${app.i18n.lang}`).data("lottie");
      endAnimLottie.setMarker("START")
      endAnimLottie.setFrame(0);
      endAnimLottie.stateMachineLoad('StateMachine1');
      endAnimLottie.stateMachineStart();
      // endAnimLottie.play();
    })

    this.currentTimeline.add(() => this.bringPionToZone(result), 1)

    // Show score
    this.$el.find(`#score-C`).text(finalScoreC);
    this.$el.find(`#score-S`).text(finalScoreS);
    this.currentTimeline.fromTo(this.$el.find(`.score`), {
      autoAlpha: 0,
      scale: 0,
    }, {
      autoAlpha: 1,
      scale: 1,
      stagger: 0.5,
      duration: 0.75,
      ease: "bounce.out",
    }, 6)

    // Bring in CTA
    this.currentTimeline.add(CerveauApp.bringInCTA(".seve-cta"))
  }

  stopEndAnimation() {
    // Stop pions animation
    this.$el.find("#brainmaze-end-animation canvas").each((i, n) => {
      const lottie = $(n).data("lottie");
      lottie.stop();
    })

    // Hide animation div
    gsap.set(this.$el.find("#brainmaze-end-animation"), {autoAlpha: 0});

    this.locked = false;
  }

  show() {
    if (this.isShown) return;

    gsap.to(this.$el, {
      autoAlpha: 1,
      duration: 0.35,
    })

    // Move in pion from root
    gsap.to(this.$pion, {
      x: this.$currentSlot.data("x"),
      y: this.$currentSlot.data("y"),
    });

    gsap.fromTo(this.$el.find(".wrapper"), {
      scale: 1.15,
    }, {
      scale: 1,
      duration: 0.75,
    })

    this.isShown = true;
  }

  hide() {
    if (!this.isShown) return;
    gsap.to(this.$el, {
      autoAlpha: 0,
      duration: 0.35,
    })
    this.isShown = false;
  }

  gracefulReset() {

    const tl = gsap.timeline();

    tl.to(this.$el, {
      autoAlpha: 0,
      duration: 0.35,
    })

    tl.add(() => this.reset())

    tl.to(this.$el, {
      autoAlpha: 1,
      duration: 0.35,
    })

  }

  highlightPath() {
    $(".brainmaze-slot.in-path").removeClass("in-path");
    const slotIndexOnPath = this.currentPath.findIndex((item) => item === this.$currentSlot.data("slot-code"))
    for (let i = slotIndexOnPath; i < this.currentPath.length; i++) {
      const segmentOptions = this.currentPath[i].split(SEPERATOR)
      for (const segmentOption of segmentOptions) {
        $(`.brainmaze-slot[data-slot-code="${segmentOption}"]`).addClass("in-path");
      }
    }
  }

  getRandomDiceResult(type, brassageIndex, forceDice = null) {
    const brassage = mazeData.brassages[constrain(brassageIndex, 0, mazeData.brassages.length)];
    const options = brassage.dicesOptions[type.toUpperCase()];
    const randomSelection = Math.floor(Math.random() * options.length);

    // Manage forbidden dice sequences and take other option
    const diceSequenceAsString = [...this.diceSequence, options[randomSelection]].join(",");
    if (mazeData.forbidden_sequences.includes(diceSequenceAsString)) {
      forceDice = options[(randomSelection + 1) % options.length];
    }

    const selectedResult = (forceDice !== null) ? forceDice : options[randomSelection];
    const selectedPath = brassage.paths.find((path) => {
      return ((path.length - 1) === selectedResult && (this.currentTargetCode == null || this.currentTargetCode === path[0]))
    })
    return {
      type: type,
      dice: selectedResult,
      path: selectedPath,
    }
  }

  snooze(visibility) {
    gsap.killTweensOf($("#brainmaze-snooze"));
    if (!visibility) {
      gsap.to($("#brainmaze-snooze"), {
        autoAlpha: 0,
        ease: "sine.out",
        onComplete: () => {
          $("#brainmaze-snooze video").get(0).pause()
          $("#brainmaze-snooze video").get(0).currentTime = 0;
        }
      });
    } else {
      $("#brainmaze-snooze video").get(0).play()
      gsap.to($("#brainmaze-snooze"), {
        autoAlpha: 1,
        ease: "sine.out",
      });
    }
  }

  reset() {

    // Reset variable and timeline
    BaseGameMNS2.killTimeline(this.currentTimeline);

    this.draggable.disable();

    // Reset slots
    const defaultSlotEl = this.getSlotElForCode("E9");
    const defaultPionPosition = {x: defaultSlotEl.data("x"), y: defaultSlotEl.data("y") + slotSize};
    this.selectSlot(defaultSlotEl.get(0))

    this.currentPath = null;
    this.currentTargetCode = null;
    this.currentTargetIndex = null;

    this.diceSequence = [];

    gsap.set(this.$pion, defaultPionPosition);
    gsap.set(this.$el.find(`.score`), {autoAlpha: 0})
    this.hideTarget()
    this.selectBoard("initial")
    this.snooze(false)

    gsap.set(this.$el.find(`.big-end-animation`), {autoAlpha: 0});
    this.$el.find(`.big-end-animation`).each((i,n) => {
      const dotLottie = $(n).data("lottie");
      dotLottie.pause()
      dotLottie.setFrame(0);
    })

    // Reset animation
    // gsap.set("#brainmaze-pink-overlay", {autoAlpha: 0});
    this.stopEndAnimation();
  }

}
